import { Box, Container } from '@mui/material';
import { PageTitle } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';
import { Configure, Index, InstantSearch } from 'react-instantsearch';

import { PageLayout } from '~/components/PageLayout/PageLayout';

import { SearchBar } from './components/SearchBar/SearchBar';
import { SearchContextProvider } from './components/SearchContextProvider';
import { SearchDomainConfigProvider } from './components/SearchDomainConfigProvider';
import { SearchEmpty } from './components/SearchEmpty/SearchEmpty.tsx';
import { SearchErrorNotification } from './components/SearchErrorNotification/SearchErrorNotification.tsx';
import { SearchHubbleTracking } from './components/SearchHubbleTracking/SearchHubbleTracking.tsx';
import { SearchResults } from './components/SearchResults/SearchResults';
import { SearchResultTabs } from './components/SearchResultTabs/SearchResultTabs';
import { useSearchConfig } from './hooks/useSearchConfig/useSearchConfig';
import { useSearchDomains } from './hooks/useSearchDomains/useSearchDomains.ts';
import { SearchDomain } from './types/SearchDomain.ts';
import { SearchResultsVirtual } from './virtual/SearchResultsVirtual/SearchResultsVirtual.tsx';

export interface SearchProps {
  domain?: SearchDomain;
}

// split in internal component needed, as hooks need to be used within SearchProvider
const SearchInternal = ({ domain }: SearchProps) => {
  const searchConfig = useSearchConfig();
  const { searchDomains } = useSearchDomains();
  const { instantSearchProps, searchConfigByDomain } = searchConfig;
  const { t } = useTranslation(undefined, { keyPrefix: 'SearchDialog' });
  return (
    <Container disableGutters>
      <InstantSearch {...instantSearchProps}>
        <SearchHubbleTracking />
        <SearchErrorNotification />
        <Configure ruleContexts={[]} />
        <PageLayout>
          <PageTitle title={t('title')} />
          <SearchDomainConfigProvider value={searchConfig}>
            <SearchBar domain={domain} />
            {domain ? <SearchResultTabs domain={domain} /> : <SearchEmpty />}
            <Box
              sx={{
                pt: 5,
                flexGrow: 1,
              }}
            >
              {searchDomains.map((searchDomain) => {
                // instant-search derives the needed queries from the actual components used.
                // To not have a miss-match in its internal state, we need to make sure to always
                // have the same components on the page. As we only display the results/filters
                // of one index at a time we need to use virtual rendering for the others
                const { indexName } = searchConfigByDomain(searchDomain);
                const Component = searchDomain === domain ? SearchResults : SearchResultsVirtual;
                return (
                  <Index key={searchDomain} indexName={indexName}>
                    <Component domain={searchDomain} />
                  </Index>
                );
              })}
            </Box>
          </SearchDomainConfigProvider>
        </PageLayout>
      </InstantSearch>
    </Container>
  );
};

export const Search = ({ domain }: SearchProps) => {
  return (
    <SearchContextProvider>
      <SearchInternal domain={domain} />
    </SearchContextProvider>
  );
};
